import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { gbp, fmtDate } from "./bookingFormat";

const POLICY_TEXT = {
  full: "48+ hours' notice: full refund",
  half: "24–48 hours' notice: 50% refund",
  none: "Less than 24 hours' notice: no refund",
};

const isRefundOnly = (q) => q.hours_notice == null || q.website;

const noticeText = (q) => {
  if (q.hours_notice == null) return "Never booked in Calendly - refund only";
  return `${q.hours_notice}h notice${q.calendly_active || q.website ? " (if cancelled now)" : " (patient already cancelled in Calendly)"}`;
};

const confirmLabel = (busy, invalid, pence) => {
  if (busy) return "Working…";
  if (invalid) return "Cancel & refund";
  return pence ? `Cancel & refund ${gbp(pence)}` : "Cancel without refund";
};

const QuoteSummary = ({ quote }) => (
  <div className="p-4 bg-white border border-bronze-light/40 rounded-md">
    <div data-testid="refund-policy">{POLICY_TEXT[quote.policy]}</div>
    <div className="mt-1 text-ink-muted">
      {noticeText(quote)} ·
      paid {gbp(quote.amount)} · suggested <b data-testid="refund-suggested">{gbp(quote.suggested)}</b>
    </div>
  </div>
);

const RefundFields = ({ quote, pounds, setPounds, reason, setReason }) => (
  <div className="space-y-4 text-sm">
    <QuoteSummary quote={quote} />
    <label className="block">
      <span className="text-xs uppercase tracking-[0.16em] text-ink-muted">Refund amount (£)</span>
      <input type="number" step="0.01" min="0" max={quote.amount / 100} value={pounds}
        onChange={(e) => setPounds(e.target.value)} data-testid="refund-amount-input"
        className="mt-1 w-full rounded-sm border border-stone-300 bg-white px-3 py-2" />
    </label>
    <label className="block">
      <span className="text-xs uppercase tracking-[0.16em] text-ink-muted">Reason (optional{isRefundOnly(quote) ? "" : ", shown in Calendly"})</span>
      <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300}
        data-testid="refund-reason-input" className="mt-1 w-full rounded-sm border border-stone-300 bg-white px-3 py-2" />
    </label>
    <p className="text-xs text-ink-muted">
      {isRefundOnly(quote) ? "This refunds the card through Stripe and emails the patient. " : "This cancels the Calendly appointment, refunds the card through Stripe and emails the patient. "}It can&apos;t be undone.
    </p>
  </div>
);

const toPence = (pounds) => Math.round(parseFloat(pounds || "0") * 100);
const isInvalid = (quote, pounds, pence) => !quote || pounds.trim() === "" || Number.isNaN(pence) || pence < 0 || pence > quote.amount;

export const CancelRefundDialog = ({ row, onClose, onDone }) => {
  const [quote, setQuote] = useState(null);
  const [pounds, setPounds] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!row) return;
    setQuote(null); setError(""); setReason("");
    api.get(`/admin/bookings/${row.id}/refund-quote`)
      .then(({ data }) => { setQuote(data); setPounds((data.suggested / 100).toFixed(2)); })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [row]);

  const pence = toPence(pounds);
  const invalid = isInvalid(quote, pounds, pence);

  const confirm = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/admin/bookings/${row.id}/cancel-refund`, { refund_amount: pence, reason: reason || null });
      toast.success(pence ? `Cancelled and refunded ${gbp(pence)}` : "Appointment cancelled (no refund)");
      onDone();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      onDone(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="cancel-refund-dialog" className="bg-cream-50 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Cancel & refund</DialogTitle>
          <DialogDescription>
            {row?.patient_name} · {fmtDate(row?.start_time)}
          </DialogDescription>
        </DialogHeader>
        {!quote && !error && <div className="text-sm text-ink-muted">Working out the refund…</div>}
        {quote && <RefundFields quote={quote} pounds={pounds} setPounds={setPounds} reason={reason} setReason={setReason} />}
        {error && <div data-testid="refund-error" className="text-sm text-destructive">{error}</div>}
        <DialogFooter>
          <button type="button" onClick={onClose} disabled={busy} className="btn-secondary" data-testid="refund-cancel-btn">Keep booking</button>
          <button type="button" onClick={confirm} disabled={busy || invalid} data-testid="refund-confirm-btn"
            className="btn-primary bg-red-700 hover:bg-red-800 disabled:opacity-50">
            {confirmLabel(busy, invalid, pence)}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
